import { useContext, useEffect, useRef, useState } from 'react';

import { Context } from '../../../context/UserContext';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import * as EmpresaAPI from '../../../api/empresa';
import * as DataListAPI from '../../../api/dataList';
import CrudsAPI from '../../../api/CRUDs';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { resolveEmpresaTabela } from '../../../utils/funcoes';
import styles from '../../form/Form.module.css';

import CredencialGeradaDialog from './CredencialGeradaDialog';
import Form from './Form';

function montarOpcao(item) {
    if (!item?.id) return null;

    return {
        ...item,
        texto: `${item.descricao || ''}`,
    };
}

function CreateOrEdit() {
    const { parametro } = useContext(Context);
    const { empresaId } = getEmpresaScope();
    const empresaTabelaId = resolveEmpresaTabela(parametro, empresaId);
    const urlParams = new URLSearchParams(window.location.search);
    const uuid = urlParams.get('uuid');
    const editando = Boolean(uuid && uuid !== 'null');

    const [dados, setDados] = useState({
        empresaId,
        codigo: '',
        descricao: '',
        empresaIds: [Number(empresaId)],
    });
    const [optionsEmpresa, setOptionsEmpresa] = useState([]);
    const [credencialGerada, setCredencialGerada] = useState(null);
    const [carregando, setCarregando] = useState(editando);
    const [falhaCarregamento, setFalhaCarregamento] = useState(false);
    const [token] = useState(localStorage.getItem('token') || '');
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const [opcoesConfiguracao, setOpcoesConfiguracao] = useState({});
    const consultasConfiguracao = useRef({});
    const [salvando, setSalvando] = useState(false);

    const [transformacoesPorEmpresa, setTransformacoesPorEmpresa] = useState({});
    const sequenciaTransformacoes = useRef(0);

    async function buscarOpcoesConfiguracao(empresaId, campo, filtro) {
        const chave = `${empresaId}:${campo}`;
        const sequencia = (consultasConfiguracao.current[chave] || 0) + 1;
        consultasConfiguracao.current[chave] = sequencia;

        try {
            const resultado = await DataListAPI.dataList({
                empresaId: campo === 'usuario' ? Number(empresaId) : Number(empresaTabelaId),
                tabela: campo === 'usuario' ? 'Pessoa' : 'Operacao',
                condicao: campo === 'usuario' ? 'pessoaUsuario' : null,
                filtro: filtro.trim(),
            });

            // A API atual retorna o erro, em vez de lançá-lo.
            if (!Array.isArray(resultado)) {
                throw new Error('Não foi possível consultar as opções.');
            }

            if (consultasConfiguracao.current[chave] !== sequencia) return;

            setOpcoesConfiguracao((anterior) => ({
                ...anterior,
                [chave]: resultado.map(montarOpcao).filter(Boolean),
            }));
        } catch (error) {
            if (consultasConfiguracao.current[chave] !== sequencia) return;

            setOpcoesConfiguracao((anterior) => ({
                ...anterior,
                [chave]: [],
            }));

            setFlashMessage(error.message || 'Falha ao consultar opções.', 'error');
        }
    }

    function alterarConfiguracao(empresaId, campo, texto) {
        const chave = `${empresaId}:${campo}`;
        const selecionado = (opcoesConfiguracao[chave] || []).find((item) => item.texto === texto);

        const campoId = campo === 'usuario' ? 'usuarioId' : 'operacaoRecebimentoId';

        const campoTexto = campo === 'usuario' ? 'usuarioTexto' : 'operacaoTexto';

        setDados((anterior) => {
            const configuracoes = [...(anterior.configuracoes || [])];
            const indice = configuracoes.findIndex((item) => Number(item.empresaId) === Number(empresaId));

            const atual =
                indice >= 0
                    ? configuracoes[indice]
                    : {
                          empresaId: Number(empresaId),
                          usuarioId: null,
                          operacaoRecebimentoId: null,
                          transformacaoFiscalId: null,
                          transformacaoNaoFiscalId: null,
                          usuarioTexto: '',
                          operacaoTexto: '',
                      };

            const atualizado = {
                ...atual,
                [campoTexto]: texto,
                [campoId]: selecionado?.id || null,
            };

            if (
                campo === 'operacao' &&
                Number(atual.operacaoRecebimentoId || 0) !== Number(atualizado.operacaoRecebimentoId || 0)
            ) {
                atualizado.transformacaoFiscalId = null;
                atualizado.transformacaoNaoFiscalId = null;
            }

            if (indice >= 0) {
                configuracoes[indice] = atualizado;
            } else {
                configuracoes.push(atualizado);
            }

            return { ...anterior, configuracoes };
        });

        // Invalida consultas anteriores mesmo quando houve seleção.
        consultasConfiguracao.current[chave] = (consultasConfiguracao.current[chave] || 0) + 1;

        if (!selecionado) {
            buscarOpcoesConfiguracao(empresaId, campo, texto);
        }
    }

    const chaveTransformacoes = JSON.stringify(
        (dados.empresaIds || [])
            .map(Number)
            .sort((a, b) => a - b)
            .map((empresaId) => {
                const configuracao = (dados.configuracoes || []).find((item) => Number(item.empresaId) === empresaId);

                return [empresaId, Number(configuracao?.operacaoRecebimentoId) || null];
            })
    );

    function alterarTransformacao(empresaId, campo, value) {
        const camposPermitidos = ['transformacaoFiscalId', 'transformacaoNaoFiscalId'];

        if (!camposPermitidos.includes(campo)) return;

        setDados((anterior) => ({
            ...anterior,
            configuracoes: (anterior.configuracoes || []).map((item) =>
                Number(item.empresaId) === Number(empresaId) ? { ...item, [campo]: value ? Number(value) : null } : item
            ),
        }));
    }

    useEffect(() => {
        if (!editando || !token || !uuid) return;

        let ativo = true;
        const sequencia = ++sequenciaTransformacoes.current;
        const empresas = JSON.parse(chaveTransformacoes);

        async function carregar() {
            const inicial = {};

            for (const [empresaId, operacaoId] of empresas) {
                inicial[empresaId] = {
                    operacaoId,
                    carregando: Boolean(operacaoId),
                    erro: '',
                    opcoes: [],
                };
            }

            setTransformacoesPorEmpresa(inicial);

            await Promise.all(
                empresas.map(async ([empresaId, operacaoId]) => {
                    if (!operacaoId) return;

                    let estado;

                    try {
                        const response = await CrudsAPI.integracaoCliente.listarTransformacoesRecebimento(
                            uuid,
                            empresaId,
                            operacaoId
                        );

                        if (!Array.isArray(response.data.data)) {
                            throw new Error('Resposta inválida ao consultar transformações.');
                        }

                        estado = {
                            operacaoId,
                            carregando: false,
                            erro: '',
                            opcoes: response.data.data,
                        };
                    } catch (error) {
                        estado = {
                            operacaoId,
                            carregando: false,
                            erro:
                                error.response?.data?.message || error.message || 'Não foi possível consultar as transformações.',
                            opcoes: [],
                        };
                    }

                    if (!ativo || sequencia !== sequenciaTransformacoes.current) return;

                    setTransformacoesPorEmpresa((anterior) => ({
                        ...anterior,
                        [empresaId]: estado,
                    }));
                })
            );
        }

        carregar();

        return () => {
            ativo = false;
        };
    }, [editando, token, uuid, chaveTransformacoes]);

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        EmpresaAPI.empresa(empresaId)
            .then(setOptionsEmpresa)
            .catch((error) => {
                setFlashMessage(
                    error.response?.data?.message || 'Não foi possível consultar as empresas.',
                    error.response?.data?.type || 'error'
                );
            });
    }, [empresaId, token]);

    useEffect(() => {
        if (!token || !editando) return;

        async function carregarEmpresasPermitidas() {
            setCarregando(true);
            setFalhaCarregamento(false);

            try {
                const response = await CrudsAPI.integracaoCliente.listarEmpresasPermitidas(uuid);
                const empresaPrincipalId = Number(response.data.meta.empresaPrincipalId);
                const empresasPermitidasIds = [
                    ...new Set([...response.data.data.map((item) => Number(item.empresaId)), empresaPrincipalId]),
                ];

                setDados((anterior) => ({
                    ...anterior,
                    empresaId: empresaPrincipalId,
                    empresaIds: empresasPermitidasIds,
                    configuracoes: response.data.data.map((item) => ({
                        empresaId: Number(item.empresaId),
                        usuarioId: item.usuarioId || null,
                        operacaoRecebimentoId: item.operacaoRecebimentoId || null,
                        transformacaoFiscalId: item.transformacaoFiscalId || null,
                        transformacaoNaoFiscalId: item.transformacaoNaoFiscalId || null,
                        usuarioTexto: montarOpcao(item.UsuarioIntegracao)?.texto || '',
                        operacaoTexto: montarOpcao(item.OperacaoRecebimento)?.texto || '',
                    })),
                }));
            } catch (error) {
                setFalhaCarregamento(true);
                setFlashMessage(
                    error.response?.data?.message || 'Não foi possível consultar as empresas permitidas.',
                    error.response?.data?.type || 'error'
                );
            } finally {
                setCarregando(false);
            }
        }

        carregarEmpresasPermitidas();
    }, [editando, setFlashMessage, token, uuid]);

    async function managerDados(novosDados) {
        if (salvando) return;

        setSalvando(true);

        try {
            if (editando) {
                const permitidas = new Set(novosDados.empresaIds.map(Number));

                const configuracoes = (novosDados.configuracoes || [])
                    .filter((item) => permitidas.has(Number(item.empresaId)))
                    .map((item) => {
                        const usuarioId = item.usuarioId || null;
                        const operacaoRecebimentoId = item.operacaoRecebimentoId || null;

                        if ((item.usuarioTexto?.trim() && !usuarioId) || (item.operacaoTexto?.trim() && !operacaoRecebimentoId)) {
                            throw new Error(`Selecione uma opção válida na configuração da empresa ${item.empresaId}.`);
                        }

                        if (Boolean(usuarioId) !== Boolean(operacaoRecebimentoId)) {
                            throw new Error(`Informe usuário e operação na empresa ${item.empresaId}, ou deixe ambos vazios.`);
                        }

                        return {
                            empresaId: Number(item.empresaId),
                            usuarioId,
                            operacaoRecebimentoId,
                            transformacaoFiscalId: item.transformacaoFiscalId || null,
                            transformacaoNaoFiscalId: item.transformacaoNaoFiscalId || null,
                        };
                    });

                const response = await CrudsAPI.integracaoCliente.substituirEmpresasPermitidas(
                    uuid,
                    novosDados.empresaIds,
                    configuracoes
                );

                setFlashMessage(response.data.message, response.data.type);
                navigate('/IntegracaoClientes');
                return;
            }

            const response = await CrudsAPI.integracaoCliente.cadastrar(novosDados);
            setCredencialGerada(response.data.data);
            setFlashMessage(response.data.message, response.data.type);
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || error.message || 'Não foi possível salvar a credencial.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setSalvando(false);
        }
    }

    function concluir() {
        setCredencialGerada(null);
        navigate('/IntegracaoClientes');
    }

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            <Form
                dados={dados}
                setDados={setDados}
                handleSubmit={managerDados}
                OptionsEmpresa={optionsEmpresa}
                caminhoVoltar="/IntegracaoClientes"
                editando={editando}
                carregando={carregando || salvando}
                falhaCarregamento={falhaCarregamento}
                opcoesConfiguracao={opcoesConfiguracao}
                onAlterarConfiguracao={alterarConfiguracao}
                onBuscarOpcoesConfiguracao={buscarOpcoesConfiguracao}
                transformacoesPorEmpresa={transformacoesPorEmpresa}
                onAlterarTransformacao={alterarTransformacao}
            />

            <CredencialGeradaDialog open={Boolean(credencialGerada)} credencial={credencialGerada} onClose={concluir} />
        </section>
    );
}

export default CreateOrEdit;
